import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ChatBotIcon from '../Images/chat-bot-icon.svg';
import UserChatIcon from '../Images/user-chat-icon.svg';
import useAuthenticatedAxios from "../auth/AuthenticatedAxios";
import { useAuth0 } from '@auth0/auth0-react';
import DOMPurify from 'dompurify';
import ImavenLoadingScreen from '../Components/Helpers/loading-screen'

const SynthoraContainer = styled.div`
  display: flex;
  height: 93vh;
  width: 100%;
  background: linear-gradient(180deg, #0f1419 0%, #1a1f2e 100%);
  color: #FFFFFF;
  padding: 20px;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  gap: 20px;
`;


const LeftPanel = styled.div`
  width: 50%;
  height: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const RightPanel = styled.div`
  width: 50%;
  height: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
`;

const Title = styled.h2`
  font-size: 24px;
  margin-bottom: 20px;
  color: ${props => props.color || '#00C851cb'};
`;

const TextArea = styled.textarea`
  background-color: rgba(25, 30, 45, 0.8);
  border: none;
  border-left: 4px solid ${props => props.borderColor || '#6366f1'};
  border-radius: 6px;
  padding: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  resize: none;
  height: 200px;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
  
  &:focus {
    outline: none;
    border-left-color: ${props => props.borderColor || '#818cf8'};
    box-shadow: 0 0 20px rgba(99, 102, 241, 0.15);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const Button = styled.button`
  background-color: rgba(25, 30, 45, 0.8);
  color: rgba(200, 210, 220, 0.9);
  border: 1px solid rgba(100, 150, 200, 0.2);
  border-radius: 6px;
  padding: 12px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: rgba(99, 102, 241, 0.15);
    color: rgba(255, 255, 255, 0.95);
    border-color: rgba(99, 102, 241, 0.4);
  }
  
  &:active {
    background-color: rgba(99, 102, 241, 0.25);
    color: #ffffff;
  }
  
  &:disabled {
    background-color: rgba(25, 30, 45, 0.5);
    border-color: rgba(100, 150, 200, 0.1);
    color: rgba(150, 160, 170, 0.4);
    cursor: not-allowed;
  }
`;

const ActionButton = styled(Button)`
  flex: 1;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 500;
  background-color: rgba(99, 102, 241, 0.1);
  color: rgba(165, 180, 252, 0.9);
  border: 1px solid rgba(99, 102, 241, 0.25);
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(99, 102, 241, 0.2);
    border-color: rgba(99, 102, 241, 0.4);
    color: #fff;
  }

  &:active {
    background-color: rgba(99, 102, 241, 0.3);
    transform: translateY(1px);
  }
`;

const SendButton = styled(Button)`
  padding: 8px 20px;
  min-width: 80px;
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: #ffffff;
  border: none;
  border-radius: 6px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);

  &:hover {
    background: linear-gradient(135deg, #818cf8 0%, #6366f1 100%);
    color: #ffffff;
    box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
    transform: translateY(-1px);
  }
  
  &:active {
    background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
    color: #ffffff;
    transform: translateY(1px);
  }
  
  &:disabled {
    background: rgba(99, 102, 241, 0.3);
    color: rgba(255, 255, 255, 0.5);
    box-shadow: none;
  }
`;

const GenerateButton = styled(Button)`
  flex: 2;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%);
  color: #a5b4fc;
  border: 2px solid rgba(99, 102, 241, 0.4);
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.15);

  &:hover {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(59, 130, 246, 0.25) 100%);
    color: #FFFFFF;
    border-color: rgba(99, 102, 241, 0.6);
    box-shadow: 0 4px 16px rgba(99, 102, 241, 0.25);
  }

  &:active {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.35) 0%, rgba(59, 130, 246, 0.35) 100%);
    transform: translateY(1px);
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.2);
  }

  &:disabled {
    background: rgba(25, 30, 45, 0.5);
    border-color: rgba(100, 150, 200, 0.15);
    color: rgba(150, 160, 170, 0.3);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ChatSimulator = styled.div`
  background: rgba(25, 30, 45, 0.6);
  border: 1px solid rgba(100, 150, 200, 0.15);
  border-radius: 12px;
  border-left: 3px solid rgba(99, 102, 241, 0.5);
  padding: 0;
  margin-top: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
`;

const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  margin: 10px 20px 10px 20px;
  min-height: 0;
  scroll-behavior: smooth;
  
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.4);
    border-radius: 4px;
    
    &:hover {
      background: rgba(99, 102, 241, 0.6);
    }
  }
`;

const MessageInputContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 15px 20px;
  gap: 12px;
  flex-shrink: 0;
  border-top: 1px solid rgba(100, 150, 200, 0.1);
  background: rgba(20, 25, 35, 0.5);
`;

const MessageInput = styled.input`
  flex-grow: 1;
  background-color: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(100, 150, 200, 0.2);
  border-radius: 8px;
  padding: 10px 15px;
  color: #FFFFFF;
  height: 40px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const ChatContainer = styled.div`
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
`;

const MessageBubble = styled.div`
  margin-bottom: 16px;
  text-align: ${props => props.isUser ? 'right' : 'left'};
  display: flex;
  align-items: flex-start;
  flex-direction: ${props => props.isUser ? 'row-reverse' : 'row'};
  gap: 10px;
`;

const MessageIcon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const Message = styled.div`
  display: inline-block;
  padding: 10px 16px;
  border-radius: 10px;
  background-color: ${props => props.isUser ? 'rgba(99, 102, 241, 0.15)' : 'rgba(30, 35, 50, 0.8)'};
  color: ${props => props.isUser ? '#e8eaed' : 'rgba(255, 255, 255, 0.85)'};
  max-width: 70%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid ${props => props.isUser ? 'rgba(99, 102, 241, 0.25)' : 'rgba(100, 150, 200, 0.1)'};
  
  /* Add styles for code blocks */
  pre {
    background-color: rgba(15, 20, 25, 0.8);
    padding: 12px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 8px 0;
    border-left: 3px solid rgba(99, 102, 241, 0.5);
  }
  
  code {
    font-family: 'Courier New', Courier, monospace;
    background-color: rgba(99, 102, 241, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
  }
`;

const RequirementSection = styled.div`
  margin-bottom: 20px;
`;

const RequirementsSection = styled.div`
  background: rgba(25, 30, 45, 0.6);
  border: 1px solid rgba(100, 150, 200, 0.15);
  border-radius: 12px;
  border-left: 3px solid rgba(99, 102, 241, 0.5);
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
`;

const DirectiveDisplayArea = styled.div`
  flex: 2;
  overflow-y: auto;
  padding: 10px 20px 20px 20px;
  max-height: 67%;
  background-color: rgba(15, 20, 30, 0.4);
  border-bottom: none;
  
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.4);
    border-radius: 4px;
    
    &:hover {
      background: rgba(99, 102, 241, 0.6);
    }
  }
  
  .directive-content {
    margin-top: 10px;
    padding: 10px 0;
    border-radius: 0;
    background-color: transparent;
    border: none;
    line-height: 1.7;
    
    h1, h2, h3, h4 {
      margin-top: 20px;
      margin-bottom: 10px;
      color: #a5b4fc;
    }
    
    ul {
      padding-left: 20px;
      margin: 12px 0;
    }
    
    li {
      margin-bottom: 8px;
    }
  }
`;

const RequirementsInputArea = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  height: 33%;
  flex: 1;
`;

const RequirementsTitle = styled(Title)`
  color: #a5b4fc;
  font-size: 24px;
  margin-bottom: 10px;
  font-weight: 600;
  display: inline-block;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.3);
`;

const QuestionMarkIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 10px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: rgba(99, 102, 241, 0.25);
  color: #a5b4fc;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  border: 1px solid rgba(99, 102, 241, 0.3);
  
  &:hover {
    background-color: rgba(99, 102, 241, 0.4);
    transform: scale(1.1);
  }
`;

const TooltipBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 9998;
  display: ${props => props.$isVisible ? 'block' : 'none'};
`;

const DirectiveTooltip = styled.div`
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  opacity: ${props => props.isVisible ? '1' : '0'};
  position: fixed;
  z-index: 9999;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 800px;
  max-width: 85vw;
  max-height: 85vh;
  overflow-y: auto;
  background: linear-gradient(135deg, rgba(25, 30, 45, 0.98) 0%, rgba(30, 35, 50, 0.98) 100%);
  color: rgba(255, 255, 255, 0.85);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid rgba(99, 102, 241, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  font-weight: normal;
  font-size: 14px;
  line-height: 1.5;
  text-align: left;
  transition: opacity 0.3s ease;
  
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(15, 20, 25, 0.5);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.4);
    border-radius: 4px;
    
    &:hover {
      background: rgba(99, 102, 241, 0.6);
    }
  }
  
  h1 {
    font-size: 22px;
    margin-bottom: 16px;
    color: #a5b4fc;
  }
  
  h2 {
    font-size: 18px;
    margin-top: 20px;
    margin-bottom: 10px;
    color: #a5b4fc;
  }
  
  h3 {
    font-size: 16px;
    margin-top: 16px;
    margin-bottom: 8px;
    color: #818cf8;
  }
  
  p {
    margin-bottom: 12px;
  }
  
  ul, ol {
    margin-bottom: 16px;
    padding-left: 20px;
  }
`;

const MainRequirementTextArea = styled(TextArea)`
  user-select: text;
  cursor: text;
  overflow-y: auto;
  background-color: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.25) !important;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.8) !important;
  margin: 10px 20px;
  line-height: 1.6;
  font-size: 14px;
  height: calc(100% - 70px);
  flex: 1;
  transition: border-color 0.2s;
  
  &:focus {
    border-color: rgba(99, 102, 241, 0.5) !important;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  padding: 10px 20px;
  margin-top: auto;
`;

const RequirementsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  padding: 0 20px 20px 20px;
`;

const AdditionalRequirementTextArea = styled(MainRequirementTextArea)`
  height: 100px;
  margin-bottom: 10px;
  background-color: rgba(25, 30, 45, 0.8) !important;
  border-left: 3px solid rgba(99, 102, 241, 0.6) !important;
  border-radius: 8px;
  padding: 12px;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const FileUploadArea = styled.div`
  border: 1px dashed rgba(99, 102, 241, 0.35);
  border-radius: 4px;
  padding: 20px;
  text-align: center;
  background-color: rgba(25, 30, 45, 0.7);
  margin-bottom: 20px;
  cursor: pointer;
  transition: all 0.2s;
  display: none;

  &:hover {
    background-color: rgba(40, 40, 40, 0.7);
  }

  input {
    display: none;
  }
`;

const FileList = styled.div`
  margin-top: 10px;
`;

const FileItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  background-color: rgba(25, 30, 45, 0.7);
  border: 1px solid rgba(100, 150, 200, 0.2);
  border-radius: 4px;
  margin-bottom: 8px;

  button {
    background: none;
    border: none;
    color: #ff4444;
    cursor: pointer;
    padding: 4px 8px;
    
    &:hover {
      color: #ff6666;
    }
  }
`;

const AddRequirementButton = styled(Button)`
  margin-bottom: 10px;
  width: 100%;
`;

const GeneratedSection = styled.div`
  background-color: rgba(187, 158, 99, 0.05);
  border: none;
  border-radius: 4px;
  border-left: 4px solid #ffbb33;
  padding: 0;
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  overflow: hidden;
`;

const GeneratedDirectiveArea = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  background-color: transparent !important;
  border: none !important;
  color: #808080 !important;
  line-height: 1.6;
  padding: 20px;
  font-size: 14px;
  user-select: text;
  max-height: 60vh;

  
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1f2e;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.4);
    border-radius: 4px;
    
    &:hover {
      background: rgba(99, 102, 241, 0.6);
    }
  }
`;

const DirectiveAreaTitle = styled.h3`
  color: #a5b4fc;
  font-size: 16px;
  margin: 10px 0 15px 0;
  font-weight: 500;
  letter-spacing: 0.3px;
`;

const ConfirmationOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const ConfirmationModal = styled.div`
    background: linear-gradient(135deg, rgba(25, 30, 45, 0.98) 0%, rgba(30, 35, 50, 0.98) 100%);
    border: 2px solid rgba(99, 102, 241, 0.35);
    border-radius: 12px;
    padding: 24px;
    width: 90%;
    max-width: 500px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(10px);
`;

const ModalTitle = styled.h3`
    color: #a5b4fc;
    font-size: 18px;
    margin-bottom: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;

    &:before {
        content: "⚠️";
        font-size: 20px;
    }
`;

const ModalContent = styled.p`
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
    line-height: 1.8;
    margin-bottom: 24px;
    padding: 12px;
    background-color: rgba(99, 102, 241, 0.1);
    border-radius: 4px;
    border-left: 3px solid rgba(99, 102, 241, 0.35);

    ul {
        list-style: none;
        margin: 10px 0;
        padding: 0;
    }

    li {
        padding-left: 20px;
        position: relative;
        margin-bottom: 8px;

        &:before {
            content: "•";
            position: absolute;
            left: 8px;
            color: #a5b4fc;
        }
    }
`;

const ModalButtons = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
`;

const ModalButtonBase = styled.button`
    padding: 8px 16px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    
    ${props => props.primary ? `
        background-color: rgba(99, 102, 241, 0.1);
        color: #a5b4fc;
        border: 2px solid rgba(99, 102, 241, 0.35);
        
        &:hover {
            background-color: rgba(99, 102, 241, 0.15);
            color: #FFFFFF;
        }
    ` : `
        background-color: transparent;
        color: rgba(255, 255, 255, 0.8);
        border: 1px solid rgba(255, 255, 255, 0.2);
        
        &:hover {
            background-color: rgba(255, 255, 255, 0.1);
            border-color: rgba(255, 255, 255, 0.3);
        }
    `}
`;

const WelcomeOverlay = styled(ConfirmationOverlay)``;

const WelcomeModal = styled(ConfirmationModal)`
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    border: 1px solid rgba(99, 102, 241, 0.25);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);

    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: #1a1f2e;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(99, 102, 241, 0.4);
        border-radius: 4px;
        
        &:hover {
            background: rgba(99, 102, 241, 0.6);
        }
    }
`;

const WelcomeTitle = styled(ModalTitle)`
    font-size: 24px;
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(99, 102, 241, 0.15);
    color: #a5b4fc;
    
    &:before {
        content: "⚠️";
        font-size: 20px;
        color: #a5b4fc;
    }
`;

const SectionTitle = styled.h4`
    font-size: 16px;
    margin: 20px 0 8px;
    font-weight: 500;
    
    &.requirements {
        color: #a5b4fc;
    }
    
    &.directive {
        color: #a5b4fc;
    }
    
    &.test {
        color: #a5b4fc;
    }
    
    &.tips {
        color: #a5b4fc;
    }
`;

const WelcomeContent = styled.div`
    color: rgba(255, 255, 255, 0.8);
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 20px;

    ul {
        list-style: none;
        padding-left: 16px;
        margin: 8px 0;

        li {
            position: relative;
            margin-bottom: 6px;
            
            &:before {
                content: "•";
                position: absolute;
                left: -16px;
            }
        }

        &.requirements li:before {
            color: rgba(160, 170, 180, 0.8);
        }
        
        &.directive li:before {
            color: rgba(160, 170, 180, 0.8);
        }
        
        &.test li:before {
            color: rgba(160, 170, 180, 0.8);
        }
        
        &.tips li:before {
            color: rgba(160, 170, 180, 0.6);
        }
    }
`;

const CloseButton = styled(ModalButtonBase)`
    margin-left: auto;
    padding: 10px 24px;
    font-weight: 600;
    background-color: rgba(99, 102, 241, 0.1);
    color: #a5b4fc;
    border: 2px solid rgba(99, 102, 241, 0.35);

    &:hover {
        background-color: rgba(99, 102, 241, 0.15);
        color: #FFFFFF;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }

    &:active {
        transform: translateY(1px);
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    }
`;

const ActionButtonBase = styled.button`
    flex: 1;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 500;
    background-color: rgba(99, 102, 241, 0.1);
    color: #a5b4fc;
    border: 1px solid rgba(99, 102, 241, 0.25);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: rgba(120, 130, 140, 0.15);
        border-color: rgba(99, 102, 241, 0.35);
    }
`;

const GenerateButtonBase = styled.button`
    flex: 1;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
    background-color: rgba(99, 102, 241, 0.1);
    color: #a5b4fc;
    border: 1px solid rgba(100, 150, 200, 0.2);
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    letter-spacing: 0.5px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    &:hover {
        background-color: rgba(120, 130, 140, 0.15);
        border-color: rgba(120, 130, 140, 0.6);
        transform: translateY(-1px);
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
    }

    &:active {
        transform: translateY(0);
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
    }
`;

const DirectiveTitle = styled(Title)`
  color: #a5b4fc;
  font-size: 24px;
  margin-bottom: 10px;
  font-weight: 600;
  display: inline-block;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.3);
`;

const AgentTypeSelector = styled.select`
  background-color: rgba(25, 30, 45, 0.7);
  color: #a5b4fc;
  border: 1px solid rgba(100, 150, 200, 0.2);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 180px;
  margin-bottom: 15px;

  &:hover {
    background-color: rgba(40, 40, 40, 0.7);
    border-color: rgba(120, 130, 140, 0.6);
  }

  &:focus {
    outline: none;
    border-color: rgba(120, 130, 140, 0.8);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.1);
  }

  option {
    background-color: #1a1f2e;
    color: #a5b4fc;
    padding: 8px;
  }
`;

const AgentTypeSelectorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 20px;
  margin-top: 10px;
`;

const AgentTypeSelectorLabel = styled.label`
  color: rgba(160, 170, 180, 0.8);
  font-size: 14px;
  font-weight: 500;
`;

const TestInstructionsBox = styled.div`
  background-color: rgba(66, 135, 245, 0.1);
  border-left: 3px solid rgba(66, 135, 245, 0.6);
  border-radius: 4px;
  padding: 16px;
  margin: 20px;
  color: rgba(200, 220, 240, 0.9);
  font-size: 14px;
  line-height: 1.6;
  
  h3 {
    color: rgba(66, 135, 245, 0.9);
    font-size: 16px;
    margin: 0 0 10px 0;
    font-weight: 600;
  }
  
  p {
    margin: 8px 0;
  }
  
  ul {
    margin: 8px 0;
    padding-left: 20px;
  }
  
  li {
    margin: 4px 0;
  }
`;

const ConversationTestArea = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  height: 100%;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(51, 51, 51, 0.5);
    border-radius: 4px;
    
    &:hover {
      background: rgba(99, 102, 241, 0.6);
    }
  }
`;

const ConversationInputArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 200px;
`;

const ConversationTextArea = styled.textarea`
  background-color: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(100, 150, 200, 0.2);
  border-radius: 6px;
  padding: 12px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  font-family: 'Courier New', Courier, monospace;
  resize: vertical;
  min-height: 200px;
  line-height: 1.6;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: rgba(120, 130, 140, 0.7);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.1);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const TestButton = styled(Button)`
  align-self: flex-start;
  padding: 10px 24px;
  background-color: rgba(66, 135, 245, 0.15);
  color: rgba(66, 135, 245, 0.95);
  border: 1px solid rgba(66, 135, 245, 0.5);
  font-weight: 600;
  
  &:hover:not(:disabled) {
    background-color: rgba(66, 135, 245, 0.25);
    border-color: rgba(66, 135, 245, 0.7);
    color: rgba(100, 160, 255, 1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ResponseDisplayArea = styled.div`
  background-color: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.25);
  border-radius: 8px;
  padding: 16px;
  margin-top: 12px;
  min-height: 150px;
  max-height: 400px;
  overflow-y: auto;
  color: rgba(200, 220, 240, 0.9);
  font-size: 14px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-wrap: break-word;
  
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(100, 150, 200, 0.2);
    border-radius: 4px;
    
    &:hover {
      background: rgba(120, 130, 140, 0.6);
    }
  }
  
  &:empty:before {
    content: 'Agent response will appear here...';
    color: rgba(255, 255, 255, 0.3);
    font-style: italic;
  }

  /* Rendered HTML Summary Styles */
  &.summary-rendered {
    white-space: normal;
    background: linear-gradient(135deg, rgba(30, 35, 45, 0.95) 0%, rgba(20, 25, 35, 0.98) 100%);
    border: 1px solid rgba(66, 135, 245, 0.2);
    
    .summary-html-content {
      h1, h2, h3, h4, h5, h6 {
        color: rgba(66, 165, 245, 0.95);
        margin: 16px 0 10px 0;
        font-weight: 600;
        
        &:first-child {
          margin-top: 0;
        }
      }
      
      h1 { font-size: 1.5em; }
      h2 { font-size: 1.3em; }
      h3 { font-size: 1.15em; }
      
      p {
        margin: 10px 0;
        color: rgba(200, 220, 240, 0.9);
      }
      
      ul, ol {
        margin: 10px 0;
        padding-left: 24px;
        
        li {
          margin: 6px 0;
          color: rgba(200, 220, 240, 0.85);
        }
      }
      
      strong, b {
        color: rgba(255, 255, 255, 0.95);
        font-weight: 600;
      }
      
      em, i {
        color: rgba(200, 220, 240, 0.8);
      }
      
      table {
        width: 100%;
        border-collapse: collapse;
        margin: 12px 0;
        
        th, td {
          border: 1px solid rgba(99, 102, 241, 0.25);
          padding: 8px 12px;
          text-align: left;
        }
        
        th {
          background: rgba(66, 135, 245, 0.15);
          color: rgba(66, 165, 245, 0.95);
          font-weight: 600;
        }
        
        tr:nth-child(even) {
          background: rgba(255, 255, 255, 0.03);
        }
      }
      
      hr {
        border: none;
        border-top: 1px solid rgba(99, 102, 241, 0.25);
        margin: 16px 0;
      }
      
      a {
        color: rgba(66, 165, 245, 0.95);
        text-decoration: none;
        
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
`;

const ObserverTestContainer = styled.div`
  display: flex;
  height: 100%;
  gap: 15px;
  padding: 20px;
`;

const ObserverConversationPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: rgba(25, 30, 45, 0.7);
  border: 1px solid rgba(99, 102, 241, 0.25);
  border-radius: 8px;
  overflow: hidden;
`;

const ObserverInsightsPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: rgba(25, 30, 45, 0.7);
  border: 1px solid rgba(99, 102, 241, 0.25);
  border-radius: 8px;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  background-color: rgba(25, 30, 45, 0.7);
  padding: 12px 16px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.25);
  color: #a5b4fc;
  font-weight: 600;
  font-size: 14px;
`;

const ConversationDisplay = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(100, 150, 200, 0.2);
    border-radius: 4px;
  }
`;

const MessageBubbleStyled = styled.div`
  padding: 10px 14px;
  border-radius: 8px;
  max-width: 85%;
  word-wrap: break-word;
  font-size: 13px;
  line-height: 1.5;
  
  ${props => props.type === 'user' ? `
    align-self: flex-start;
    background-color: rgba(66, 135, 245, 0.15);
    border-left: 3px solid rgba(66, 135, 245, 0.6);
    color: rgba(200, 220, 255, 0.95);
  ` : `
    align-self: flex-end;
    background-color: rgba(76, 175, 80, 0.15);
    border-left: 3px solid rgba(76, 175, 80, 0.6);
    color: rgba(200, 255, 220, 0.95);
  `}
  
  .timestamp {
    font-size: 10px;
    opacity: 0.6;
    margin-top: 4px;
  }
  
  .label {
    font-weight: 600;
    font-size: 11px;
    margin-bottom: 4px;
    opacity: 0.8;
  }
`;

const InputArea = styled.div`
  padding: 12px;
  border-top: 1px solid rgba(99, 102, 241, 0.25);
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: rgba(25, 30, 45, 0.6);
`;

const InputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const SmallInput = styled.input`
  flex: 1;
  background-color: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(100, 150, 200, 0.2);
  border-radius: 4px;
  padding: 8px 12px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: rgba(120, 130, 140, 0.7);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const SmallButton = styled.button`
  padding: 8px 16px;
  background-color: rgba(66, 135, 245, 0.15);
  color: rgba(66, 135, 245, 0.95);
  border: 1px solid rgba(66, 135, 245, 0.5);
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background-color: rgba(66, 135, 245, 0.25);
    border-color: rgba(66, 135, 245, 0.7);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InsightCard = styled.div`
  /* Match workspace Observer styling exactly */
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-left: 4px solid ${props => {
    // Priority-based colors - same as workspace Observer
    switch(props.priority?.toLowerCase()) {
      case 'high': return '#ff4444cb';  // Red
      case 'medium': return '#ffbb33cb'; // Yellow/Orange
      case 'low': return '#00C851cb';    // Green
      default: return '#00C851cb';       // Default green
    }
  }};
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 6px;
  font-size: 12px;
  line-height: 1.5;
  font-family: 'Arial', sans-serif;
  
  .timestamp {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.4);
    margin-top: 8px;
    text-align: right;
  }

  /* Table styles for formatted content */
  table {
    border-collapse: collapse;
    width: 100%;
    border: none !important;
  }
  
  tr, td {
    border: none !important;
    background: none !important;
  }
  
  td {
    vertical-align: top;
    padding: 0 !important;
    line-height: 1.5;
  }
  
  td:first-child {
    color: rgba(255, 255, 255, 0.6);
    font-weight: 500;
    padding-right: 10px !important;
    white-space: nowrap;
  }
  
  td:last-child {
    color: rgba(255, 255, 255, 0.9);
  }

  /* AutoResponse link styling */
  a, .auto-response-content {
    color: rgb(82, 180, 252) !important;
    text-decoration: underline !important;
    cursor: pointer;
  }
`;

const InsightsDisplay = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
  min-height: 0; /* Important for flex children to scroll properly */
  
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(100, 150, 200, 0.2);
    border-radius: 4px;
    
    &:hover {
      background: rgba(120, 130, 140, 0.6);
    }
  }
  
  &:empty:before {
    content: 'Observer insights will appear here as you add messages...';
    color: rgba(255, 255, 255, 0.3);
    font-style: italic;
    display: block;
    text-align: center;
    padding: 40px 20px;
  }
`;




const Synthora = () => {

  const { user, getAccessTokenSilently, isAuthenticated } = useAuth0();
  const authenticatedAxios = useAuthenticatedAxios();

  const [requirements, setRequirements] = useState('');
  const [requirementsList, setRequirementsList] = useState([]);
  const [generatedDirective, setGeneratedDirective] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);

  const [files, setFiles] = useState([]);
  const [isMessageSending, setIsMessageSending] = useState(false);

  // Add state to control tooltip visibility
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = React.useRef(null);

  // Add state for agent type selection
  const [selectedAgentType, setSelectedAgentType] = useState('widget');
  
  // Add state for Observer and Summarizer testing inputs
  const [observerUserInput, setObserverUserInput] = useState('');
  const [observerInsights, setObserverInsights] = useState([]);
  const [isObserverLoading, setIsObserverLoading] = useState(false);
  const [summarizerConversationInput, setSummarizerConversationInput] = useState('');
  const [summarizerResponse, setSummarizerResponse] = useState('');
  const [isTestingAgent, setIsTestingAgent] = useState(false);

  // Function to clean markdown code blocks
  const cleanMarkdownCodeBlocks = (content) => {
    if (!content) return '';
    // Remove ```html and ``` markers
    return content.replace(/```(html|javascript|css|jsx|json|xml|plaintext|bash|shell)?\n/g, '').replace(/```/g, '');
  };

  useEffect(() => {
    const getGeneratedDirective = async () => {
      setGeneratedDirective('');
      try {
        const response = await authenticatedAxios.get(`/synthora/getDirective?agentType=${selectedAgentType}`);
        if (response.data && response.data.data) {
          setGeneratedDirective(response.data.data);
          if (window.plausible) {
            window.plausible('GetGeneratedDirective', {
              props: {
                userId: user?.sub,
                directiveLength: response.data.data.length,
                agentType: selectedAgentType
              }
            });
          }
        } else {
          // Default directive if none exists in the database
          const agentName = selectedAgentType === 'widget' ? 'Widget' : 
                           selectedAgentType === 'observer' ? 'Observer' : 
                           selectedAgentType === 'summarizer' ? 'Summarizer' : 'Agent';
          setGeneratedDirective(`# Welcome to ${agentName} Agent Directive Setup\n\nNo directive has been created yet for the ${agentName} agent. You can write your requirements in the left panel and click 'Generate Directive' to create your first directive.`);
        }
      } catch (error) {
        console.error('Error fetching directive:', error);
        // Default directive in case of error
        const agentName = selectedAgentType === 'widget' ? 'Widget' : 
                         selectedAgentType === 'observer' ? 'Observer' : 
                         selectedAgentType === 'summarizer' ? 'Summarizer' : 'Agent';
        setGeneratedDirective(`# Welcome to ${agentName} Agent Directive Setup\n\nUnable to load existing directive. You can write your requirements in the left panel and click 'Generate Directive' to create a new directive.`);
      } finally {
        localStorage.setItem('threadId', 'none');
      }
    }

    const startChat = async () => {
      try {
        const response = await authenticatedAxios.post('/synthora/startChat');
        // Check if response.data exists and contains the expected property
        if (response.data) {
          // Fix potential typo: convesationId -> conversationId
          const conversationId = response.data.conversationId || response.data.convesationId;
          if (conversationId) {
            localStorage.setItem('conversationId', conversationId);
          } else {
            console.warn('No conversation ID received from server');
          }
        }
        
        // Add a welcome message in the chat
        setChatHistory([{
          text: "Welcome! This is the test environment for your Widget Agent. Type a message below to simulate how your agent will respond based on the directive you've created. Feel free to test different scenarios!",
          isUser: false,
          icon: ChatBotIcon
        }]);
      } catch (error) {
        console.error('Error starting chat:', error);
        
        // Add an error message in the chat if startup failed
        setChatHistory([{
          text: "Welcome to the Widget Agent test environment! There was an issue connecting to the server, but you can still write requirements and generate directives.",
          isUser: false,
          icon: ChatBotIcon
        }]);
      }
    }
    getGeneratedDirective();
    startChat();
  }, [selectedAgentType]);

  const handleFileUpload = (event) => {
    const newFiles = Array.from(event.target.files);
    setFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (fileName) => {
    setFiles(prev => prev.filter(file => file.name !== fileName));
  };

  const addRequirement = () => {
    const newId = Date.now();
    setRequirementsList(prev => [...prev, { id: newId, content: '' }]);

    setRequirements(prev => {
      if (!prev.includes('Critical:')) {
        return prev + '\n\nCritical:';
      }
      return prev;
    });
  };

  const updateRequirement = (id, value) => {
    setRequirementsList(prev => prev.map(req =>
      req.id === id ? { ...req, content: value } : req
    ));

    setRequirements(prev => {
      const lines = prev.split('\n');
      const criticalIndex = lines.findIndex(line => line.includes('Critical:'));

      const criticalPoints = requirementsList.map(req => {
        if (req.id === id) {
          return value ? `• ${value}` : null;
        }
        return req.content ? `• ${req.content}` : null;
      }).filter(Boolean);

      const beforeCritical = lines.slice(0, criticalIndex + 1).join('\n');
      return beforeCritical + '\n' + criticalPoints.join('\n');
    });
  };

  const removeRequirement = (index) => {
    const reqToRemove = requirementsList[index];
    setRequirementsList(prev => prev.filter((_, i) => i !== index));

    setRequirements(prev => {
      const mainText = prev.split('Critical:')[0].trim();
      const remainingCriticals = requirementsList
        .filter((_, i) => i !== index)
        .filter(req => req.content)
        .map(req => `• ${req.content}`)
        .join('\n');
      return remainingCriticals ? `${mainText}\n\nCritical:\n${remainingCriticals}` : mainText;
    });
  };

  const [showConfirmation, setShowConfirmation] = useState(false);


  const handleUpdateDirective = async () => {
    if (!requirements.trim()) return;

    try {
      setGeneratedDirective('');
      setIsProcessing(true);
      const response = await authenticatedAxios.post('/synthora/updateDirective', { 
        requirements,
        agentType: selectedAgentType 
      });
      const data = response.data;
      setGeneratedDirective(data.message);

      if (window.plausible) {
        window.plausible('UpdateDirective', {
          props: {
            userId: user.sub,
            directiveLength: response.data.message.length,
            agentType: selectedAgentType
          }
        });
      }

    } catch (error) {
      console.error('Error updating directive:', error);
      // Show error message in the directive display area
      setGeneratedDirective("# Error Updating Directive\n\nThere was a problem updating your directive. Please try again or refresh the page.\n\nError details: " + (error.message || "Unknown error"));
      
      // Add an error message to the chat
      setChatHistory(prev => [...prev, {
        text: "I'm sorry, there was an error updating the directive. Please try again.",
        isUser: false,
        icon: ChatBotIcon
      }]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGenerateDirective = () => {
    if (!requirements.trim()) return;
    setShowConfirmation(true);
  };

  const handleConfirm = async () => {
    setShowConfirmation(false);
    setIsProcessing(true);

    setGeneratedDirective('');
    try {
      const response = await authenticatedAxios.post('/synthora/generateDirective', { 
        requirements,
        agentType: selectedAgentType 
      });
      const data = response.data;
      setGeneratedDirective(data.directive);
      if (window.plausible) {
        window.plausible('GenerateDirective', {
          props: {
            userId: user.sub,
            directiveLength: data.directive.length,
            agentType: selectedAgentType
          }
        });
      }
      // Just remove the UI components, keep all text as is
      setRequirementsList([]);
      // Don't modify requirements state - keep all text!
      clearChatHistory();
      
      // Add success message to chat
      setChatHistory([{
        text: "A new directive has been generated! You can now test how your Widget Agent responds based on your requirements. Type a message to begin testing.",
        isUser: false,
        icon: ChatBotIcon
      }]);
    } catch (error) {
      console.error('Error generating directive:', error);
      // Show error message in the directive display area
      setGeneratedDirective("# Error Generating Directive\n\nThere was a problem generating your directive. Please try again or refresh the page.\n\nError details: " + (error.message || "Unknown error"));
      
      // Add an error message to the chat
      setChatHistory(prev => [...prev, {
        text: "I'm sorry, there was an error generating the directive. Please try again.",
        isUser: false,
        icon: ChatBotIcon
      }]);
    } finally {
      setIsProcessing(false);
    }
  };

  const clearChatHistory = async () => {
    try {
      const response = await authenticatedAxios.post('/synthora/startChat');
      // Fix potential typo: convesationId -> conversationId
      const conversationId = response.data.conversationId || response.data.convesationId;
      if (conversationId) {
        localStorage.setItem('conversationId', conversationId);
      } else {
        console.warn('No conversation ID received from server');
      }
      // Reset chat and add welcome message
      setChatHistory([{
        text: "Chat history cleared. Start a new conversation to test your Widget Agent with the updated directive.",
        isUser: false,
        icon: ChatBotIcon
      }]);
      localStorage.setItem('threadId', 'none');
      setChatInput('');
    } catch (error) {
      console.error('Error starting chat:', error);
      setChatHistory([{
        text: "There was an issue restarting the chat, but I've cleared your history. Let's continue our conversation.",
        isUser: false,
        icon: ChatBotIcon
      }]);
      localStorage.setItem('threadId', 'none');
      setChatInput('');
    }
  }

  const chatMessagesRef = React.useRef(null);
  const messagesEndRef = React.useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end"
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  const handleSendMessage = async () => {
    if (!chatInput.trim() || isMessageSending) return;

    const userMessage = {
      text: chatInput,
      isUser: true,
      icon: UserChatIcon
    };

    setChatHistory(prev => [...prev, userMessage]);
    setIsMessageSending(true);
    scrollToBottom();

    try {
      setChatInput('');

      const conversationId = localStorage.getItem('conversationId');
      const threadId = localStorage.getItem('threadId') || 'none';

      const response = await authenticatedAxios.post('/synthora/chat', {
        chatInput: userMessage.text,
        threadId: threadId,
        conversationId: conversationId
      });

      if (response.data && response.data.threadId) {
        localStorage.setItem('threadId', response.data.threadId);
      }

      setChatHistory(prev => [...prev, {
        text: response.data?.message || "Sorry, I couldn't process your request at this time.",
        isUser: false,
        icon: ChatBotIcon
      }]);

      scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);

      // Add a system message informing the user about the error
      setChatHistory(prev => [...prev, {
        text: "I'm sorry, there was an error processing your message. Please try again later.",
        isUser: false,
        icon: ChatBotIcon
      }]);

      scrollToBottom();
    } finally {
      setIsMessageSending(false);
    }
  };

  const [isProcessing, setIsProcessing] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(() => {
    // Check localStorage when component mounts
    return localStorage.getItem('hideWelcomePopup') !== 'true';
  });

  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleWelcomeClose = () => {
    if (dontShowAgain) {
      // Save preference to localStorage
      localStorage.setItem('hideWelcomePopup', 'true');
    }
    setShowWelcomePopup(false);
  };

  // Handle testing Observer agent - single input, one-time check
  const handleTestObserver = async () => {
    if (!observerUserInput.trim() || isObserverLoading) return;
    
    const userMessage = observerUserInput;
    setObserverUserInput(''); // Reset input immediately
    setIsObserverLoading(true);
    
    // Create message for Observer to analyze
    const messages = [{ type: 'user', text: userMessage }];
    
    await generateObserverInsight(messages);
    setIsObserverLoading(false);
  };

  // Format Observer message exactly like workspace Observer does
  const formatObserverMessage = (htmlString) => {
    const div = document.createElement('div');
    div.innerHTML = htmlString;
    const text = div.textContent;
    
    // Handle Priority
    const priorityMatch = text.match(/\[Priority:\s*(High|Medium|Low)\]/i);
    const priority = priorityMatch ? priorityMatch[1] : 'Low';
    let modifiedHtml = htmlString;
    
    // Hide the priority and type tags
    modifiedHtml = modifiedHtml.replace(/\[Priority:\s*(High|Medium|Low)\]/i, '');
    modifiedHtml = modifiedHtml.replace(/\[Type:\s*(.*?)\]/i, '');
    
    // Replace Emotion tags
    const emotionMatch = text.match(/Emotion\[(.*?)\]/i);
    const emotion = emotionMatch ? emotionMatch[1].toLowerCase().trim() : 'neutral';
    modifiedHtml = modifiedHtml.replace(/Emotion\[(.*?)\]/i, '');
    
    // Create a table layout for the entire content (same as workspace)
    let processedHtml = '<table style="width:100%; border-spacing:0; border-collapse:collapse; font-size:12px;">';
    
    // Check if we have Context section
    const contextMatch = modifiedHtml.match(/(Context:)\s*(.*?)(?=\s*Suggestion:|$)/s);
    if (contextMatch) {
      processedHtml += `
        <tr>
          <td style="color:rgba(255,255,255,0.6); padding-bottom:6px; vertical-align:top; white-space:nowrap; width:90px;">Context:</td>
          <td style="color:rgba(255,255,255,0.9); padding-bottom:6px; padding-left:5px; vertical-align:top;">${contextMatch[2].trim()}</td>
        </tr>
      `;
    }
    
    // Check if we have Suggestion section
    const suggestionMatch = modifiedHtml.match(/(Suggestion:)\s*(.*?)(?=\s*Emotion|AutoResponse|$)/s);
    if (suggestionMatch) {
      processedHtml += `
        <tr>
          <td style="color:rgba(255,255,255,0.6); padding-bottom:6px; vertical-align:top; white-space:nowrap; width:90px;">Suggestion:</td>
          <td style="color:rgba(255,255,255,0.9); padding-bottom:6px; padding-left:5px; vertical-align:top;">${suggestionMatch[2].trim()}</td>
        </tr>
      `;
    }
    
    // Check if we have AutoResponse section
    const autoResponseMatch = modifiedHtml.match(/(AutoResponse:)\s*"(.*?)"/s);
    if (autoResponseMatch) {
      const content = autoResponseMatch[2];
      processedHtml += `
        <tr>
          <td style="color:rgba(255,255,255,0.6); vertical-align:top; white-space:nowrap; width:90px;">AutoResponse:</td>
          <td style="padding-left:5px; vertical-align:top;">
            <span style="color:rgb(82,180,252);">"${content}"</span>
          </td>
        </tr>
      `;
    }
    
    processedHtml += '</table>';
    
    return { priority, emotion, formattedHtml: processedHtml };
  };

  const generateObserverInsight = async (messages) => {
    // Simple test endpoint - no database, no threadId, just test data
    try {
      const response = await authenticatedAxios.post('/synthora/testObserver', {
        messages: messages,
        agentType: 'observer'
      });
      
      // Parse the response the same way workspace Observer does
      let processedMessage = response.data.data;
      if (typeof processedMessage === 'string') {
        processedMessage = processedMessage.replace(/^```html\n/, '').replace(/\n```$/, '');
      }
      
      // Skip "[Got the info]" messages
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = processedMessage;
      const textContent = tempDiv.textContent.trim();
      
      if (textContent === "[Got the info]") {
        return;
      }
      
      // Format message exactly like workspace Observer
      const { priority, emotion, formattedHtml } = formatObserverMessage(processedMessage);
      
      const insight = {
        priority: priority,
        emotion: emotion,
        formattedHtml: formattedHtml,
        timestamp: new Date().toLocaleTimeString()
      };
      
      setObserverInsights(prev => {
        const newInsights = [insight, ...prev];
        return newInsights.slice(0, 5);
      });
    } catch (error) {
      console.error('Error getting Observer insight:', error);
      const errorHtml = `
        <table style="width:100%; border-spacing:0; border-collapse:collapse; font-size:12px;">
          <tr>
            <td style="color:rgba(255,255,255,0.6); padding-bottom:6px; vertical-align:top; white-space:nowrap; width:90px;">Error:</td>
            <td style="color:rgba(255,100,100,0.9); padding-bottom:6px; padding-left:5px; vertical-align:top;">Unable to get Observer analysis</td>
          </tr>
          <tr>
            <td style="color:rgba(255,255,255,0.6); padding-bottom:6px; vertical-align:top; white-space:nowrap; width:90px;">Suggestion:</td>
            <td style="color:rgba(255,255,255,0.9); padding-bottom:6px; padding-left:5px; vertical-align:top;">Please check your directive and try again.</td>
          </tr>
        </table>
      `;
      setObserverInsights(prev => [{
        priority: 'High',
        emotion: 'neutral',
        formattedHtml: errorHtml,
        timestamp: new Date().toLocaleTimeString()
      }, ...prev].slice(0, 5));
    }
  };

  const clearObserverTest = () => {
    setObserverInsights([]);
    setObserverUserInput('');
  };

  // Handle testing Summarizer agent
  const handleTestSummarizer = async () => {
    if (!summarizerConversationInput.trim()) {
      setSummarizerResponse('Please provide a complete conversation to test the Summarizer agent.');
      return;
    }

    setIsTestingAgent(true);
    setSummarizerResponse('');
    
    try {
      // Simple test endpoint - no database, no threadId, just test data
      const response = await authenticatedAxios.post('/synthora/testSummarizer', {
        conversation: summarizerConversationInput,
        agentType: 'summarizer'
      });
      
      // Use the summary from response (same parsing as workspace)
      setSummarizerResponse(response.data.summary);
      
    } catch (error) {
      console.error('Error testing Summarizer:', error);
      setSummarizerResponse('Error: Unable to test Summarizer agent. Please check your directive and try again.\n\nDetails: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsTestingAgent(false);
    }
  };



  // Add click outside handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target) && 
          !event.target.closest('.question-mark-icon')) {
        setShowTooltip(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <SynthoraContainer>
      <LeftPanel>
        <RequirementsSection>
          <div style={{ paddingLeft: '20px', paddingRight: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '15px' }}>
            <RequirementsTitle>
              Requirements
            </RequirementsTitle>
            <QuestionMarkIcon 
              className="question-mark-icon" 
              onClick={() => setShowTooltip(!showTooltip)}
            >
              ?
            </QuestionMarkIcon>
            <TooltipBackdrop $isVisible={showTooltip} onClick={() => setShowTooltip(false)} />
            {showTooltip && (
              <DirectiveTooltip 
                ref={tooltipRef}
                isVisible={showTooltip} 
                onClick={(e) => e.stopPropagation()}
              >
                <h1>Quick Guide to Senthora: Customizing Your iMaven AI Assistant</h1>
                
                <h2>Welcome to Senthora!</h2>
                
                <p>Senthora is your playground for personalizing your iMaven AI assistant. This guide will walk you through how to customize your AI assistant to better serve your clients with personalized guidance in finance, health, shopping, or other advisory areas.</p>
                
                <h2>What is Senthora?</h2>
                
                <p>Senthora is a customization tool that allows you, as an adviser, to tailor how your AI assistant communicates with clients. You simply describe what you want in everyday language, and Senthora's special AI assistant transforms your ideas into structured "directives" that guide your assistant's behavior.</p>
                
                <h2>Using the Senthora Interface</h2>
                
                <p>The interface has three main sections:</p>
                <ol>
                  <li><strong>Requirements Panel</strong> (Left) - Where you write your instructions</li>
                  <li><strong>Test Your Assistant</strong> (Right) - Where you see how your assistant will behave</li>
                  <li><strong>Control Buttons</strong> (Bottom) - Options to update or generate directives</li>
                </ol>
                
                <h2>Creating vs. Updating Directives</h2>
                
                <p>Senthora gives you two options for customizing your AI assistant:</p>
                
                <h3>Example 1: Creating a New Directive with Heart Emojis</h3>
                
                <p>Let's walk through creating a new directive:</p>
                
                <ol>
                  <li><strong>Write Your Instruction</strong>: In the Requirements panel (left side), type: "I want my assistant to end every message with a heart emoji"</li>
                  <li><strong>Generate the Directive</strong>: Click the "Generate Directive" button at the bottom of the screen</li>
                  <li><strong>See the Transformation</strong>: Watch as your simple instruction gets transformed into a structured directive with a title, purpose, and specific instructions</li>
                  <li><strong>Test the Result</strong>: Look at the Test Your Assistant panel (right side) - you'll notice the AI assistant now ends its message with a ❤️</li>
                  <li><strong>Try a Conversation</strong>: Type a test message in the input box at the bottom right and press send. Every response from your assistant will now end with a heart emoji</li>
                </ol>
                
                <h3>Example 2: Updating an Existing Directive</h3>
                
                <p>Now let's update our directive:</p>
                
                <ol>
                  <li><strong>Review Current Directive</strong>: Look at the heart emoji directive in the Requirements panel</li>
                  <li><strong>Add Update Instructions</strong>: Below the existing text, type: "Also make the assistant address the client by name at the beginning of each message"</li>
                  <li><strong>Update the Directive</strong>: Click the "Update Directive" button</li>
                  <li><strong>See the Changes</strong>: Notice how the system incorporates your new requirements while preserving the existing heart emoji requirement</li>
                  <li><strong>Test the Updated Behavior</strong>: In the test panel, type a message that includes your name. The assistant will now address you by name AND end with a heart emoji</li>
                  <li><strong>Reset for Testing</strong>: Click "Clear Chat History" if you want to start a fresh conversation with your updated assistant</li>
                </ol>
                
                <p>You can continue this cycle of updating and testing until your assistant behaves exactly as you want it to.</p>
                
                <h2>Tips for Effective Instructions</h2>
                
                <h3>Power Words That Get Results</h3>
                <ul>
                  <li><strong>"Critical" or "Always"</strong> - For must-happen behaviors
                    <ul><li>Example: "Critical: Always greet users by name"</li></ul>
                  </li>
                  <li><strong>"Never" or "Do not"</strong> - To set clear boundaries
                    <ul><li>Example: "Never overwhelm users with too many options"</li></ul>
                  </li>
                  <li><strong>"Important"</strong> - For high priority (but not absolute) requirements
                    <ul><li>Example: "It's important to keep responses friendly"</li></ul>
                  </li>
                  <li><strong>"When/If...then..."</strong> - For conditional behaviors
                    <ul><li>Example: "When users seem confused, then offer simpler explanations"</li></ul>
                  </li>
                </ul>
                
                <h3>Testing and Refining</h3>
                <ol>
                  <li>Make one change at a time</li>
                  <li>Test with different questions</li>
                  <li>Clear chat history between tests</li>
                  <li>Refine based on what you see</li>
                </ol>
                
                <p>Remember: Your AI assistant will follow your instructions, so be clear about what matters most to you and your clients!</p>
              </DirectiveTooltip>
            )}
          </div>

          {/* Agent Type Selector */}
          <AgentTypeSelectorContainer>
            <AgentTypeSelectorLabel>Agent Type:</AgentTypeSelectorLabel>
            <AgentTypeSelector 
              value={selectedAgentType} 
              onChange={(e) => setSelectedAgentType(e.target.value)}
            >
              <option value="widget">Widget Agent</option>
              <option value="observer">Observer Agent</option>
              <option value="summarizer">Summarizer Agent</option>
            </AgentTypeSelector>
          </AgentTypeSelectorContainer>

          {/* First Area: Directive Display with Scrolling */}
          <DirectiveDisplayArea>
            <DirectiveAreaTitle>Generated Directive:</DirectiveAreaTitle>
            {generatedDirective.length > 0 ? (
              <div className="directive-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleanMarkdownCodeBlocks(generatedDirective)) }} />
            ) : (
              <ImavenLoadingScreen />
            )}
          </DirectiveDisplayArea>

          {/* Second Area: Requirements Input and Buttons */}
          <RequirementsInputArea>
            <MainRequirementTextArea
              value={requirements}
              onChange={(e) => setRequirements(e.target.value)}
              placeholder="Enter your main requirements here..."
            />

            <ButtonContainer style={{ justifyContent: 'space-between', padding: '10px 20px', marginTop: 'auto' }}>
              <GenerateButtonBase
                onClick={handleUpdateDirective}
                disabled={isProcessing || !requirements.trim()}
                style={{
                  padding: '8px 16px',
                }}
              >
                {isProcessing ? 'Updating...' : 'Update Directive'}
              </GenerateButtonBase>

              <GenerateButtonBase
                onClick={handleGenerateDirective}
                disabled={isProcessing || !requirements.trim()}
                style={{
                  padding: '8px 16px',
                }}
              >
                {isProcessing ? 'Generating...' : 'Generate Directive'}
              </GenerateButtonBase>
            </ButtonContainer>
          </RequirementsInputArea>
        </RequirementsSection>
      </LeftPanel>

      <RightPanel>
        {selectedAgentType === 'widget' && (
          <ChatSimulator>
            <div style={{ padding: '20px 20px 10px 20px' }}>
              <DirectiveTitle>
                Test Widget Agent
              </DirectiveTitle>
            </div>
            
            <TestInstructionsBox>
              <h3>How to Test Your Widget Agent</h3>
              <p>This area allows you to simulate how your Widget Agent will respond to user messages based on the directive you've created.</p>
              <p><strong>Instructions:</strong> Type a message below and see how your agent responds. The agent will follow the directive you've defined in the left panel.</p>
            </TestInstructionsBox>
            
            <ChatMessages ref={chatMessagesRef}>
              {chatHistory.map((message, index) => (
                <MessageBubble
                  key={index}
                  isUser={message.isUser}
                  isStreaming={!message.isUser && index === chatHistory.length - 1}
                >
                  <MessageIcon
                    src={message.icon}
                    alt={message.isUser ? "User" : "Assistant"}
                  />
                  <Message
                    isUser={message.isUser}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message.isUser ? message.text : cleanMarkdownCodeBlocks(message.text)) }}
                  />
                </MessageBubble>
              ))}
              <div ref={messagesEndRef} />
            </ChatMessages>

            <MessageInputContainer>
              <Button
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  color: '#a5b4fc',
                  border: '1px solid rgba(99, 102, 241, 0.35)',
                  transition: 'background-color 0.2s, color 0.2s',
                }}
                onClick={clearChatHistory}
                disabled={isMessageSending}
                onMouseEnter={(e) => {
                  if (!isMessageSending) {
                    e.target.style.backgroundColor = 'rgba(99, 102, 241, 0.15)';
                    e.target.style.color = '#FFFFFF';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isMessageSending) {
                    e.target.style.backgroundColor = 'rgba(99, 102, 241, 0.1)';
                    e.target.style.color = '#a5b4fc';
                  }
                }}
              >
                Clear Chat History
              </Button>
              <MessageInput
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Type a message to test..."
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                disabled={isMessageSending}
              />
              <SendButton
                onClick={handleSendMessage}
                disabled={isMessageSending}
              >
                {isMessageSending ? 'Sending...' : 'Send'}
              </SendButton>
            </MessageInputContainer>
          </ChatSimulator>
        )}

        {selectedAgentType === 'observer' && (
          <ChatSimulator>
            <div style={{ padding: '20px 20px 10px 20px' }}>
              <DirectiveTitle>
                Test Observer Agent
              </DirectiveTitle>
            </div>
            
            <TestInstructionsBox>
              <h3>How to Test Your Observer Agent</h3>
              <p>The Observer provides REAL-TIME insights during live conversations between advisers and users.</p>
              <p><strong>Instructions:</strong> Simulate a conversation by adding user messages and adviser responses. The Observer will analyze and provide insights as the conversation progresses, just like in the workspace.</p>
            </TestInstructionsBox>
            
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '16px', overflow: 'hidden', minHeight: 0 }}>
              {/* Input Area - Simple one input */}
              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                marginBottom: '16px',
                alignItems: 'center'
              }}>
                <input
                  value={observerUserInput}
                  onChange={(e) => setObserverUserInput(e.target.value)}
                  placeholder="Enter user message to analyze..."
                  onKeyPress={(e) => e.key === 'Enter' && handleTestObserver()}
                  disabled={isObserverLoading}
                  style={{
                    flex: 1,
                    backgroundColor: 'rgba(25, 30, 45, 0.8)',
                    border: '1px solid rgba(99, 102, 241, 0.25)',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    color: 'rgba(200, 220, 240, 0.9)',
                    fontSize: '14px',
                    outline: 'none',
                    fontFamily: 'inherit'
                  }}
                />
                <TestButton 
                  onClick={handleTestObserver}
                  disabled={isObserverLoading || !observerUserInput.trim()}
                  style={{ 
                    minWidth: '120px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {isObserverLoading ? (
                    <>
                      <span style={{ 
                        display: 'inline-block',
                        width: '14px',
                        height: '14px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      Analyzing...
                    </>
                  ) : 'Analyze'}
                </TestButton>
                <Button 
                  onClick={clearObserverTest}
                  style={{ 
                    padding: '12px 16px',
                    fontSize: '13px',
                    backgroundColor: 'rgba(100, 100, 100, 0.3)'
                  }}
                >
                  Clear
                </Button>
              </div>

              {/* Observer Insights Panel - Full width with scroll */}
              <div style={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0, /* Critical for flex children scroll */
                background: 'linear-gradient(180deg, rgba(25, 30, 45, 0.9) 0%, rgba(20, 25, 40, 0.95) 100%)',
                backdropFilter: 'blur(10px)',
                borderRadius: '8px',
                border: '1px solid rgba(99, 102, 241, 0.15)'
              }}>
                <PanelHeader style={{ padding: '12px 16px', borderBottom: '1px solid rgba(99, 102, 241, 0.15)', flexShrink: 0 }}>
                  Observer Insights
                </PanelHeader>
                
                <InsightsDisplay style={{ flex: 1, padding: '16px', minHeight: 0, overflowY: 'auto' }}>
                  {isObserverLoading && (
                    <div style={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '40px',
                      color: 'rgba(66, 135, 245, 0.9)'
                    }}>
                      <div style={{ 
                        width: '40px',
                        height: '40px',
                        border: '3px solid rgba(66, 135, 245, 0.2)',
                        borderTop: '3px solid rgba(66, 135, 245, 0.9)',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginBottom: '16px'
                      }} />
                      <div>Analyzing conversation...</div>
                    </div>
                  )}
                  
                  {!isObserverLoading && observerInsights.length === 0 && (
                    <div style={{ 
                      textAlign: 'center', 
                      color: 'rgba(255, 255, 255, 0.3)', 
                      fontStyle: 'italic',
                      padding: '40px 20px'
                    }}>
                      Enter a user message and click "Analyze" to get Observer insights...
                    </div>
                  )}
                  
                  {!isObserverLoading && observerInsights.map((insight, index) => (
                    <InsightCard key={index} priority={insight.priority}>
                      <div 
                        dangerouslySetInnerHTML={{ 
                          __html: DOMPurify.sanitize(insight.formattedHtml) 
                        }} 
                      />
                      <div className="timestamp">{insight.timestamp}</div>
                    </InsightCard>
                  ))}
                </InsightsDisplay>
              </div>
              
              {/* CSS for spinner animation */}
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          </ChatSimulator>
        )}

        {selectedAgentType === 'summarizer' && (
          <ChatSimulator style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Compact Header */}
            <div style={{ padding: '12px 16px 8px 16px', borderBottom: '1px solid rgba(99, 102, 241, 0.15)' }}>
              <DirectiveTitle style={{ fontSize: '18px', marginBottom: '4px' }}>
                Test Summarizer Agent
              </DirectiveTitle>
              <p style={{ color: 'rgba(160, 170, 180, 0.7)', fontSize: '12px', margin: 0 }}>
                Paste a conversation below and click "Test Summarizer" to generate a summary.
              </p>
            </div>
            
            {/* Two-column layout: Input on left, Result on right */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden', padding: '12px', gap: '12px' }}>
              {/* Left: Compact Input Area */}
              <div style={{ width: '35%', display: 'flex', flexDirection: 'column', minWidth: '250px' }}>
                <label style={{ color: '#a5b4fc', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                  Conversation Input:
                </label>
                <textarea
                  value={summarizerConversationInput}
                  onChange={(e) => setSummarizerConversationInput(e.target.value)}
                  placeholder="Paste the complete conversation here..."
                  style={{
                    flex: 1,
                    backgroundColor: 'rgba(25, 30, 45, 0.8)',
                    border: '1px solid rgba(99, 102, 241, 0.25)',
                    borderRadius: '6px',
                    padding: '10px',
                    color: 'rgba(200, 220, 240, 0.9)',
                    fontSize: '12px',
                    lineHeight: '1.5',
                    resize: 'none',
                    outline: 'none',
                    fontFamily: 'inherit'
                  }}
                />
                <TestButton 
                  onClick={handleTestSummarizer}
                  disabled={isTestingAgent || !summarizerConversationInput.trim()}
                  style={{ marginTop: '10px', width: '100%' }}
                >
                  {isTestingAgent ? 'Summarizing...' : 'Test Summarizer'}
                </TestButton>
              </div>

              {/* Right: Large Result Area */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <label style={{ color: '#a5b4fc', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                  Generated Summary:
                </label>
                <ResponseDisplayArea className="summary-rendered" style={{ flex: 1, maxHeight: 'none', margin: 0 }}>
                  {isTestingAgent ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(66, 135, 245, 0.8)' }}>
                      <div>Generating summary...</div>
                    </div>
                  ) : summarizerResponse ? (
                    <div 
                      className="summary-html-content"
                      dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(
                          summarizerResponse
                            .replace(/^```html\n?/, '')
                            .replace(/\n?```$/, '')
                            .replace(/```(html|javascript|css|jsx|json|xml|plaintext|bash|shell)?\n?/g, '')
                            .replace(/```/g, '')
                        ) 
                      }} 
                    />
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(160, 170, 180, 0.5)', fontStyle: 'italic' }}>
                      Summary will appear here after you click "Test Summarizer"
                    </div>
                  )}
                </ResponseDisplayArea>
              </div>
            </div>
          </ChatSimulator>
        )}
      </RightPanel>
      {showConfirmation && (
        <ConfirmationOverlay>
          <ConfirmationModal>
            <ModalTitle>Warning: Generate New Directive</ModalTitle>
            <ModalContent>
              <p>You are about to generate a new directive. This action will:</p>
              <ul>
                <li>Override your existing directive</li>
                <li>Replace all current instructions</li>
                <li>Cannot be undone</li>
              </ul>
              <p>Are you sure you want to proceed?</p>
            </ModalContent>
            <ModalButtons>
              <ModalButtonBase onClick={() => setShowConfirmation(false)}>
                Cancel
              </ModalButtonBase>
              <ModalButtonBase primary onClick={handleConfirm}>
                Generate
              </ModalButtonBase>
            </ModalButtons>
          </ConfirmationModal>
        </ConfirmationOverlay>
      )}
      {showWelcomePopup && (
        <WelcomeOverlay>
          <WelcomeModal>
            <WelcomeTitle>Welcome to Synthora</WelcomeTitle>
            <WelcomeContent>
              <p>This is your workspace for testing and refining AI directives. Here's what you'll find:</p>

              <SectionTitle className="requirements">Requirements Section</SectionTitle>
              <ul className="requirements">
                <li>Write your main requirements for the AI assistant</li>
                <li>Upload supporting files for context</li>
                <li>Add critical requirements for specific constraints</li>
                <li>Generate directives based on your requirements</li>
              </ul>

              <SectionTitle className="directive">Generated Directive</SectionTitle>
              <ul className="directive">
                <li>View the AI-generated directive based on your requirements</li>
                <li>Review and refine the generated instructions</li>
                <li>Ensure all key requirements are properly translated</li>
              </ul>

              <SectionTitle className="test">Test Environment</SectionTitle>
              <ul className="test">
                <li>Test your directive in a simulated chat environment</li>
                <li>Verify the AI's responses match your requirements</li>
                <li>Interact with the AI to ensure proper behavior</li>
              </ul>

              <SectionTitle className="tips">Tips</SectionTitle>
              <ul className="tips">
                <li>Be specific in your requirements</li>
                <li>Test edge cases in the chat simulator</li>
                <li>Review and iterate on your directives</li>
              </ul>
            </WelcomeContent>

            <ModalButtons style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                marginTop: '8px'
              }}>
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                />
                Don't show this message again
              </label>

              <CloseButton onClick={handleWelcomeClose}>
                Got it!
              </CloseButton>
            </ModalButtons>
          </WelcomeModal>
        </WelcomeOverlay>
      )}
    </SynthoraContainer>
  );
};

export default Synthora; 